import React, { useEffect, useState, useRef, useContext } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import axios from 'axios';
import { IoHourglassOutline, IoPeopleCircleSharp } from "react-icons/io5";
import TextToHTML from '../Components/TextToHtml';
import FluidMediaModal from '../Components/feedmediamodal';
import LoadingSpinner from '../Components/LoadingSpinner';
import LikeButton from '../Components/LikeButton';
import { useHistory, useLocation } from "react-router-dom";
import { useAtom } from 'jotai';
import config from '../Config';
import LayoutHomeUser from '../Layouts/Admin/Layout';
import DarkModeContext from '../Components/DarkModeContext';
import { isAuthAtom, authUserAtom } from '../Services/store/atoms';
import ScrollToTopButton from '../Components/ScrollToTopButton';

dayjs.extend(relativeTime);

function FeedPublico(props) {
  const idModel = props.match?.params?.idModel || '';

  const observerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [feeds, setFeeds] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const history = useHistory();
  const location = useLocation();
  const [isAuth] = useAtom(isAuthAtom);
  const [authUser] = useAtom(authUserAtom);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [selectedMediaUrls, setSelectedMediaUrls] = useState([]);
  const [mediaClickIndex, setMediaClickIndex] = useState(0);
  const { darkMode } = useContext(DarkModeContext);

  const fetchFeeds = async (pageUrl) => {
    setIsLoading(true);
    try {
      axios.defaults.headers.common.Authorization = `Bearer ${authUser.token}`;
      const response = await axios.get(pageUrl || `${config.REACT_APP_BASE_URL}/api/v1/home/FeedByUser/${idModel}`);
      const newFeeds = response.data.feeds.data.map(feed => ({
        ...feed,
        liked: false,
        likeCount: feed.likes_count || 0
      }));

      setFeeds(prevFeeds => {
        const uniqueFeeds = newFeeds.filter(newFeed =>
          !prevFeeds.some(prevFeed => prevFeed.id === newFeed.id)
        );
        return pageUrl ? [...prevFeeds, ...uniqueFeeds] : uniqueFeeds;
      });

      setNextPageUrl(response.data.feeds.next_page_url);
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setFeeds([]);
    fetchFeeds(`${config.REACT_APP_BASE_URL}/api/v1/home/FeedByUser/${idModel}`);
  }, [idModel]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    const observer = observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && nextPageUrl && !isLoading) {
        fetchFeeds(nextPageUrl);
      }
    }, { rootMargin: '0px', threshold: 0.5 });

    const target = document.querySelector('#scroll-end');
    if (target) observer.observe(target);

    return () => observer.disconnect();
  }, [nextPageUrl, isLoading]);

  const onCardClick = (userId) => {
    history.push(`/profileModal/${userId}`, { background: location });
  };

  const handleAllClick = () => {
    history.push('/FeedPublic');
  };

  const handleMediaClick = (mediaUrls, index = 0) => {
    setSelectedMediaUrls(mediaUrls);
    setMediaClickIndex(index);
    setShowMediaModal(true);
  };

  return (
    <LayoutHomeUser>
      <div className="ap-screen ap-screen-inset -m-2 lg:-m-4 p-2 lg:p-4 pb-10 min-h-[calc(100dvh-3.5rem)] flex flex-col items-center">
        {isAuth && (
          <div className="flex justify-center pt-4 pb-2">
            <button className="ap-btn ap-btn-gray ap-btn-sm" onClick={handleAllClick}>
              <IoPeopleCircleSharp className='text-lg' />
              All
            </button>
          </div>
        )}

        <div className="flex flex-col justify-center items-center w-full">
          {feeds.length === 0 ? (
            <div className="ap-subhead text-center py-16">No hay feeds disponibles.</div>
          ) : (
            feeds.map((feed, key) => (
              <div key={feed.id} className="ap-card relative mt-4 p-4 max-w-2xl w-full mx-2">
                <div className="flex items-center justify-between mb-2">
                  <a onClick={() => onCardClick(feed.user_id)} className="flex items-center cursor-pointer min-w-0">
                    <span className="w-11 h-11 rounded-full overflow-hidden shrink-0" style={{ boxShadow: '0 0 0 0.5px var(--ap-separator)' }}>
                      <img src={feed.avatar_thumb_url || feed.avatar} alt="User Avatar" className="object-cover w-full h-full" />
                    </span>
                    <span className="ml-3 min-w-0">
                      <span className="ap-headline capitalize block truncate">{feed.user_date.nickname}</span>
                      <span className="ap-footnote flex items-center gap-1">
                        <IoHourglassOutline />
                        {dayjs(feed.created_at).fromNow()}
                      </span>
                    </span>
                  </a>
                  <LikeButton feedId={feed.id} />
                </div>
                <div className="ap-body px-1 py-1">
                  <TextToHTML text={feed.description} />
                </div>
                <div className={`mt-3 w-full overflow-x-auto ${feed.files.length > 1 ? 'grid grid-cols-2 gap-2' : 'flex'}`}>
                  {feed.files.map((file, index) => (
                    file.url_thumbnail ? (
                      <img
                        key={index}
                        src={file.url_thumbnail}
                        alt={`Thumbnail ${index}`}
                        className={`w-full object-cover cursor-pointer rounded-[14px] ${(feed.files?.length || 0) > 1 ? 'aspect-square' : ''}`}
                        onClick={() => handleMediaClick(feed.files.map(file => file.url), index)}
                      />
                    ) : (
                      <div
                        key={index}
                        className="w-full h-40 animate-pulse rounded-[14px] flex items-center justify-center"
                        style={{ background: 'var(--ap-fill)' }}
                      >
                        <span className="ap-footnote">Procesando...</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-center items-center py-4">
          {isLoading && feeds.length > 0 && <LoadingSpinner />}
        </div>

        <ScrollToTopButton />

        {showMediaModal && (
          <FluidMediaModal
            show={showMediaModal}
            onClose={() => setShowMediaModal(false)}
            mediaUrls={selectedMediaUrls}
            startIndex={mediaClickIndex}
          />
        )}
      </div>
    </LayoutHomeUser>
  );
}

export default FeedPublico;
